import { cn } from '@exam-room/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Alert, Button, Form, LoadingSpinner } from '../../components';

interface ProfileConfirmationInput {
  otherEmail: string;
  confirmOtherEmail: string;
  isAdditionalEmailEnabled: boolean;
  isConfirmationEmailEnabled: boolean;
}

interface ProfileConfirmationFormProps {
  onSubmit: SubmitHandler<ProfileConfirmationInput>;
  isLoading: boolean;
  registerEmail?: string;
  options?: {
    shouldDisplayEmailField?: boolean;
  };
}

function ProfileConfirmationForm({
  onSubmit,
  isLoading,
  registerEmail,
  options,
}: ProfileConfirmationFormProps) {
  const { t } = useTranslation();
  const createProfileConfirmationSchema = () =>
    z
      .object({
        isConfirmationEmailEnabled: z.literal(true, {
          errorMap: () => ({ message: 'profileConfirmationForm.error.needUserConfirm' }),
        }),
        isAdditionalEmailEnabled: z.boolean(),
        otherEmail: z.string().optional(),
        confirmOtherEmail: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data.isAdditionalEmailEnabled) {
          if (!data.otherEmail) {
            ctx.addIssue({
              path: ['otherEmail'],
              code: z.ZodIssueCode.custom,
              message: 'profileConfirmationForm.error.fillEmail',
            });
          } else {
            try {
              z.string().email().parse(data.otherEmail);
            } catch {
              ctx.addIssue({
                path: ['otherEmail'],
                code: z.ZodIssueCode.custom,
                message: 'profileConfirmationForm.error.invalidEmail',
              });
            }
            if (data.otherEmail === registerEmail) {
              ctx.addIssue({
                path: ['otherEmail'],
                code: z.ZodIssueCode.custom,
                message: 'profileConfirmationForm.error.duplicateEmail',
              });
            }
          }

          if (!data.confirmOtherEmail) {
            ctx.addIssue({
              path: ['confirmOtherEmail'],
              code: z.ZodIssueCode.custom,
              message: 'profileConfirmationForm.error.confirmEmail',
            });
          } else {
            try {
              z.string().email().parse(data.confirmOtherEmail);
            } catch {
              ctx.addIssue({
                path: ['confirmOtherEmail'],
                code: z.ZodIssueCode.custom,
                message: 'profileConfirmationForm.error.invalidEmail',
              });
            }
            if (data.otherEmail && data.confirmOtherEmail !== data.otherEmail) {
              ctx.addIssue({
                path: ['confirmOtherEmail'],
                code: z.ZodIssueCode.custom,
                message: 'profileConfirmationForm.error.confirmEmailIncorrect',
              });
            } else if (data.confirmOtherEmail === registerEmail) {
              ctx.addIssue({
                path: ['confirmOtherEmail'],
                code: z.ZodIssueCode.custom,
                message: 'profileConfirmationForm.error.duplicateEmail',
              });
            }
          }
        }
      });

  const { control, handleSubmit, watch, setValue, setError } = useForm<ProfileConfirmationInput>({
    resolver: zodResolver(createProfileConfirmationSchema()),
    defaultValues: {
      otherEmail: '',
      confirmOtherEmail: '',
      isAdditionalEmailEnabled: false,
      isConfirmationEmailEnabled: false,
    },
  });

  const validateForm = (data: ProfileConfirmationInput): boolean => {
    const schema = createProfileConfirmationSchema();
    const result = schema.safeParse(data);

    if (!result.success) {
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof ProfileConfirmationInput;
        setError(field, { type: 'manual', message: issue.message });
      });
      return false;
    }
    return true;
  };

  const handleFormSubmit = (data: ProfileConfirmationInput, event?: React.BaseSyntheticEvent) => {
    event?.preventDefault();
    const isValid = validateForm(data);
    if (!isValid) return;

    onSubmit(data);
  };

  const isAdditionalEmailEnabledValue = watch('isAdditionalEmailEnabled');
  const isConfirmationEmailEnabledValue = watch('isConfirmationEmailEnabled');

  const handleToggleIisAdditionalEmailEnabled = () => {
    setValue('isAdditionalEmailEnabled', !isAdditionalEmailEnabledValue);
  };

  const handleToggleIsConfirmationEmailEnabled = () => {
    setValue('isConfirmationEmailEnabled', !isConfirmationEmailEnabledValue);
  };

  const shouldDisplayEmailField = options?.shouldDisplayEmailField ?? true;

  return (
    <form className='grid gap-4' onSubmit={handleSubmit(handleFormSubmit)}>
      {shouldDisplayEmailField ? (
        <>
          <div className='flex items-center justify-between rounded-lg border border-gray-300 p-4'>
            <div className='text-body1 !font-bold text-gray-800'>{registerEmail}</div>
            <p className='text-body2 !text-success-700 border-success-500 bg-success-100 cursor-default rounded-full border px-2.5'>
              {t('profileConfirmationForm.form.registerEmail')}
            </p>
          </div>
          <div className='flex flex-col items-center rounded-lg border border-gray-300 px-4 py-4'>
            <div className='flex w-full items-center justify-between'>
              <div className='text-body1 font-medium'>
                {t('profileConfirmationForm.form.otherEmail')}
              </div>
              <label
                htmlFor='email-toggle'
                className='relative inline-flex cursor-pointer items-center'
              >
                <Form.Input
                  name='isAdditionalEmailEnabled'
                  type='checkbox'
                  id='email-toggle'
                  control={control}
                  checked={isAdditionalEmailEnabledValue}
                  onChange={handleToggleIisAdditionalEmailEnabled}
                  className='sr-only'
                />
                <div
                  className={cn(
                    `relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-200 ${
                      isAdditionalEmailEnabledValue ? 'bg-warning-500' : 'bg-gray-300'
                    }`,
                  )}
                >
                  <span
                    className={cn(
                      `mx-0.5 inline-block size-5 rounded-full bg-white transition-transform duration-200 ${
                        isAdditionalEmailEnabledValue ? 'translate-x-6 transform' : ''
                      }`,
                    )}
                  ></span>
                </div>
              </label>
            </div>
            <div
              className={cn('mt-4 w-full text-left', isAdditionalEmailEnabledValue ? '' : 'hidden')}
            >
              <Form.Input
                control={control}
                id='username'
                required
                name='otherEmail'
                label={t('profileConfirmationForm.form.email')}
                type='text'
                autoComplete='username'
                className='w-full'
              />
            </div>
            <div
              className={cn('mt-4 w-full text-left', isAdditionalEmailEnabledValue ? '' : 'hidden')}
            >
              <Form.Input
                control={control}
                id='confirmOtherEmail'
                required
                name='confirmOtherEmail'
                label={t('profileConfirmationForm.form.confirmEmail')}
                type='text'
                autoComplete='username'
                className='w-full'
              />
            </div>
          </div>
          <Alert
            className='justify-center gap-6 text-left'
            variant='warning'
            message={
              <Trans
                i18nKey='profileConfirmationForm.form.warningText'
                components={[
                  <span key='warning1' />,
                  <span key='warning2' />,
                  <span key='warning3' />,
                ]}
              />
            }
          />
        </>
      ) : null}
      <div className='flex items-center gap-2'>
        <Form.Checkbox
          name='isConfirmationEmailEnabled'
          type='checkbox'
          id='confirmation-email-toggle'
          control={control}
          checked={isConfirmationEmailEnabledValue}
          onChange={handleToggleIsConfirmationEmailEnabled}
          className='accent-primary size-6 cursor-pointer rounded-md'
        >
          {t('profileConfirmationForm.form.userConfirmation')}
        </Form.Checkbox>
      </div>
      <Button
        type='submit'
        className='mx-auto mt-2 w-full'
        disabled={isLoading || !isConfirmationEmailEnabledValue}
      >
        {isLoading ? (
          <LoadingSpinner className='size-7' />
        ) : (
          t('profileConfirmationForm.form.identify')
        )}
      </Button>
    </form>
  );
}
export default ProfileConfirmationForm;
